import { useState, useEffect } from 'react';
import { useProfile } from '@/hooks/useProfile';
import { useNetWorthData } from '@/hooks/useNetWorthData';
import { useNetWorthSnapshots, useUpsertNetWorthSnapshot } from '@/hooks/useNetWorthSnapshots';
import type { TimePeriod } from '@/hooks/useNetWorthSnapshots';
import { useAuth } from '@/contexts/AuthContext';
import { NetWorthSummaryCard } from '@/components/net-worth-summary-card/NetWorthSummaryCard';
import { AssetBreakdownCard } from '@/components/asset-breakdown-card/AssetBreakdownCard';
import { LiabilityBreakdownCard } from '@/components/liability-breakdown-card/LiabilityBreakdownCard';
import { NetWorthTrendChart } from '@/components/net-worth-trend-chart/NetWorthTrendChart';
import { CurrencyToggle } from '@/components/currency-toggle/CurrencyToggle';

function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

export default function Dashboard() {
  const { user } = useAuth();
  const { data: profile } = useProfile();

  // Req 5.5: default to profile currency, fallback USD
  const [displayCurrency, setDisplayCurrency] = useState<string>('USD');

  // Once profile loads, initialise displayCurrency from profile preference
  useEffect(() => {
    if (profile?.default_currency) {
      setDisplayCurrency(profile.default_currency);
    }
  }, [profile?.default_currency]);

  const [timePeriod, setTimePeriod] = useState<TimePeriod>('1m');

  // Aggregated asset/liability data
  const {
    totalAssets,
    totalLiabilities,
    netWorth,
    assetCategories,
    liabilityCategories,
    isLoading: dataLoading,
    errors,
  } = useNetWorthData(displayCurrency);

  // Historical snapshot data
  const {
    data: snapshots = [],
    isLoading: snapshotsLoading,
  } = useNetWorthSnapshots(timePeriod);

  // Snapshot upsert mutation
  const upsertSnapshot = useUpsertNetWorthSnapshot();

  // Req 6.1 / 6.4: upsert today's snapshot when data resolves
  useEffect(() => {
    if (!user) return;
    if (dataLoading) return;
    const allClear = Object.values(errors).every((e) => e === null);
    if (!allClear) return;

    const today = new Date().toISOString().slice(0, 10);
    upsertSnapshot.mutate({
      user_id: user.id,
      snapshot_date: today,
      total_assets: totalAssets,
      total_liabilities: totalLiabilities,
      net_worth: netWorth,
      currency: displayCurrency,
      updated_at: new Date().toISOString(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataLoading, totalAssets, totalLiabilities, netWorth, displayCurrency]);

  // Req 5.4: disable currency toggle while exchange rates are loading
  const ratesLoading = dataLoading;

  const displayName = profile?.display_name || profile?.first_name || 'there';

  return (
    <div className="space-y-6">
      {/* Header with greeting */}
      <div
        className="flex items-center justify-between flex-wrap gap-4 animate-slide-up"
      >
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">
            {getGreeting()}, {displayName}
          </h1>
          <p className="text-muted-foreground text-sm">
            Your consolidated financial overview.
          </p>
        </div>
        <CurrencyToggle
          baseCurrency={displayCurrency}
          selectedCurrency={displayCurrency}
          onCurrencyChange={setDisplayCurrency}
          disabled={ratesLoading}
          showBalance={false}
        />
      </div>

      {/* Net Worth Summary */}
      <div className="animate-slide-up" style={{ animationDelay: '50ms' }}>
        <NetWorthSummaryCard
          netWorth={netWorth}
          displayCurrency={displayCurrency}
          isLoading={dataLoading}
          errors={errors}
        />
      </div>

      {/* Asset & Liability Breakdown */}
      <div className="grid gap-4 md:grid-cols-2">
        <div className="animate-slide-up" style={{ animationDelay: '100ms' }}>
          <AssetBreakdownCard
            categories={assetCategories}
            totalAssets={totalAssets}
            displayCurrency={displayCurrency}
            isLoading={dataLoading}
          />
        </div>
        <div className="animate-slide-up" style={{ animationDelay: '150ms' }}>
          <LiabilityBreakdownCard
            categories={liabilityCategories}
            totalLiabilities={totalLiabilities}
            displayCurrency={displayCurrency}
            isLoading={dataLoading}
          />
        </div>
      </div>

      {/* Net Worth Trend Chart */}
      <div className="animate-slide-up" style={{ animationDelay: '200ms' }}>
        <NetWorthTrendChart
          snapshots={snapshots}
          displayCurrency={displayCurrency}
          timePeriod={timePeriod}
          onTimePeriodChange={setTimePeriod}
          isLoading={snapshotsLoading}
        />
      </div>
    </div>
  );
}
